import {
  checkIfRevenueNumberExists,
  getNextRevenueNumber,
} from 'api/v2/revenues';
import dayjs from 'dayjs';
import useSuperQuery from 'hooks/useSuperQuery';
import { useId } from 'react';
import useQuery from 'hooks/react-query/useQuery';

import { InvoiceType } from 'types/invoice.types';
import { ONE_SECOND } from 'utils/time';

type NextNumberOptions = {
  docType: InvoiceType;
  docDate?: string;
  enabled?: boolean;
  onSuccess: (value: string) => void;
};

const today = dayjs().format('YYYY-MM-DD');

export const useNextInvoiceNumber = (options: NextNumberOptions) => {
  const date = options.docDate || today;
  return useQuery(
    ['useInvoiceNumber', useId(), dayjs(date).year()],
    () => getNextRevenueNumber({ type: options.docType, date }),
    {
      gcTime: 0,
      enabled: options.enabled,
      onSuccess: (value) => {
        if (!value) return;
        options.onSuccess(value);
      },
    },
  );
};

type ValidateNumberOptions = {
  enabled?: boolean;
  docType: InvoiceType;
  nextNumber: string;
  defaultNumber?: string;
  onValid: (value: string) => void;
  onInvalid: (value: string) => void;
};

export const useValidateInvoiceNumber = (options: ValidateNumberOptions) => {
  const { defaultNumber, nextNumber: revenueNumber, enabled } = options;

  const isEnabled =
    enabled !== false && !!revenueNumber && revenueNumber !== defaultNumber;

  return useSuperQuery({
    queryKey: [
      'useValidateInvoiceNumber',
      useId(),
      revenueNumber,
      defaultNumber,
      isEnabled,
    ],
    queryFn: () => {
      return checkIfRevenueNumberExists({
        type: options.docType,
        revenueNumber,
      })
        .then((result) => ({ exists: result, revenueNumber }))
        .catch(() => ({ exists: false, revenueNumber }));
    },
    debounceTime: ONE_SECOND / 2,
    gcTime: 0,
    staleTime: 0,
    enabled: isEnabled,
    onSuccess: (_result) => {
      const result = _result as { exists: boolean; revenueNumber: string };
      if (result.exists) {
        options.onInvalid(result.revenueNumber);
      } else {
        options.onValid(result.revenueNumber);
      }
    },
  });
};
